import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ArrowDown, ArrowUp, GripVertical } from 'lucide-react';
import { DISPLAYED_REQUEST_STATUSES, type BacklogItem, type BacklogVersionSummary, type RequestStatus } from '@product-portal/shared';
import { Button, buttonVariants } from '@/components/ui/button';
import { TeamsConcernedBadge } from '@/components/portal/duplicate-badges';
import { ImpactTag } from '@/components/portal/impact-tag';
import { PageTitle } from '@/components/portal/page-title';
import { OrderBadge, SectionCard } from '@/components/portal/section-card';
import { SelectField } from '@/components/portal/select-field';
import { StatusPill } from '@/components/portal/status-pill';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRowDrag } from '@/features/prioritization/reorder';
import { useBacklog, useHelpRequests, useProductRequests, usePublishBacklog, useSaveDraftOrder } from '@/hooks/use-product';
import { formatDayMonth, formatDate, shortName } from '@/lib/format';
import { PRIORITY_LABELS, STATUS_LABELS, TYPE_LABELS, VERSION_STATE_LABELS } from '@/lib/labels';
import { TypeTag } from '@/components/portal/type-tag';
import { cn } from '@/lib/utils';

function VersionCard({ label, version, hint, tone }: { label: string; version: BacklogVersionSummary | null; hint: string; tone?: 'accent' }) {
  return (
    <div className={cn('flex flex-col gap-1 rounded-card border px-5 py-4', tone === 'accent' ? 'border-accent-border bg-accent' : 'border-border bg-card')}>
      <span className="text-[13px] text-muted-foreground">{label}</span>
      <span className="text-[17px] font-bold">
        {version ? `${version.number} · ${version.plannedDate ? `livraison ${formatDayMonth(version.plannedDate)}` : VERSION_STATE_LABELS[version.state].toLowerCase()}` : '—'}
      </span>
      <span className="text-xs text-muted-foreground">{hint}</span>
    </div>
  );
}

/**
 * 09 · Backlog & arbitrage. The PO moves the DRAFT order (drag and drop, or
 * the arrows); « Publier le backlog » copies it into the published order, the
 * one everybody else sees. Bugs and evolutions both appear (arbitrage of
 * 2026-10-19 on mock-up 09).
 */
/**
 * « Aide & accès » of the product (ADR-011, not in mock-up 09): open help requests, handled
 * outside the backlog — no order, no publication, no export. A row opens screen 08.
 */
function HelpRequestsSection({ productId, productName }: { productId: string; productName: string }) {
  const help = useHelpRequests(productId);
  const items = help.data?.items ?? [];
  return (
    <SectionCard
      title={`Aide & accès · ${productName}`}
      toolbar={<span className="text-[13px] text-muted-foreground">Traitées hors backlog, sans ordre de priorité</span>}
    >
      <Table minWidth={760}>
        <TableHeader>
          <TableRow>
            <TableHead>Demande</TableHead>
            <TableHead>Demandeur</TableHead>
            <TableHead>Impact métier</TableHead>
            <TableHead>Statut</TableHead>
            <TableHead>Soumise le</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id}>
              <TableCell>
                <Link to={`/produit/demandes/${item.id}`} className="font-bold text-foreground no-underline hover:text-foreground">
                  {item.title}
                </Link>
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  {item.reference}
                  {item.jiraKey && ` · ${item.jiraKey}`}
                </span>
              </TableCell>
              <TableCell className="text-foreground-secondary">
                {shortName(item.requester)}
                <span className="block text-xs text-muted-foreground">{item.team.name}</span>
              </TableCell>
              <TableCell>
                <ImpactTag impact={item.impact} />
              </TableCell>
              <TableCell>
                <StatusPill status={item.status} />
              </TableCell>
              <TableCell className="whitespace-nowrap text-foreground-secondary">{formatDate(item.submittedAt)}</TableCell>
            </TableRow>
          ))}
          {help.data && items.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-muted-foreground">
                Aucune demande d'aide ou d'accès ouverte sur ce produit.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </SectionCard>
  );
}

/** « Voir tout » (2026-10-22): every request of the product, any type and status, newest first. Read only. */
function AllRequestsSection({ productId, productName }: { productId: string; productName: string }) {
  const all = useProductRequests(productId, true);
  const [status, setStatus] = useState<RequestStatus | ''>('');
  const items = (all.data?.items ?? []).filter((i) => !status || i.status === status);
  return (
    <SectionCard
      title={`Toutes les demandes · ${productName}`}
      toolbar={
        <SelectField label="Statut" value={status} onChange={(e) => setStatus(e.target.value as RequestStatus | '')}>
          <option value="">Tous les statuts</option>
          {DISPLAYED_REQUEST_STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s]}
            </option>
          ))}
        </SelectField>
      }
    >
      <Table minWidth={820}>
        <TableHeader>
          <TableRow>
            <TableHead>Demande</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Demandeur</TableHead>
            <TableHead>Statut</TableHead>
            <TableHead>Soumise le</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id}>
              <TableCell>
                <Link to={`/produit/demandes/${item.id}`} className="font-bold text-foreground no-underline hover:text-foreground">
                  {item.title}
                </Link>
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  {item.reference}
                  {item.jiraKey && ` · ${item.jiraKey}`}
                </span>
              </TableCell>
              <TableCell>
                <TypeTag type={item.type} />
              </TableCell>
              <TableCell className="text-foreground-secondary">
                {shortName(item.requester)}
                <span className="block text-xs text-muted-foreground">{item.team.name}</span>
              </TableCell>
              <TableCell>
                <StatusPill status={item.status} />
              </TableCell>
              <TableCell className="whitespace-nowrap text-foreground-secondary">{formatDate(item.submittedAt)}</TableCell>
            </TableRow>
          ))}
          {all.data && items.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-muted-foreground">
                Aucune demande{status ? ' avec ce statut' : ''} sur ce produit.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </SectionCard>
  );
}

export function BacklogPage() {
  const [productId, setProductId] = useState<string | null>(null);
  const [teamId, setTeamId] = useState('');
  // ADR-011: the help requests are a second view of the product, one click away (not stacked under the backlog).
  const [view, setView] = useState<'backlog' | 'help' | 'all'>('backlog');
  const query = useBacklog(productId);
  const data = query.data;
  const current = data?.product?.id ?? '';
  const saveOrder = useSaveDraftOrder(current);
  const publish = usePublishBacklog(current);
  const [rows, setRows] = useState<BacklogItem[]>([]);
  const serverOrder = JSON.stringify(data?.items.map((i) => i.id) ?? []);
  useEffect(() => setRows(data?.items ?? []), [serverOrder, data?.items]);
  const filtered = teamId ? rows.filter((r) => r.team.id === teamId) : rows;
  const canReorder = !teamId;
  const drag = useRowDrag(rows, (next) => {
    setRows(next);
    // Each move is saved at once, in the DRAFT order only.
    saveOrder.mutate(next.map((r) => r.id));
  });
  const firstUnplanned = filtered.findIndex((r, i) => !r.version && filtered.slice(i).every((x) => !x.version));
  const versions = data?.versions;
  const selected = data?.products.find((p) => p.id === current);
  // On arriving on a product, show the view that has something: only help → help, no help → backlog;
  // otherwise keep the view the PO was looking at.
  useEffect(() => {
    if (!selected) return;
    if (selected.count === 0 && selected.helpCount > 0) setView('help');
    else if (selected.helpCount === 0 && view === 'help') setView('backlog');
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <PageTitle
        title="Backlog & arbitrage"
        subtitle={
          data?.lastPublishedAt
            ? `Vous travaillez sur l'ordre brouillon. Les autres utilisateurs voient l'ordre publié le ${formatDate(data.lastPublishedAt)} jusqu'à la prochaine publication.`
            : "Vous travaillez sur l'ordre brouillon. Les autres utilisateurs ne voient l'ordre qu'une fois le backlog publié."
        }
      >
        {current && (
          <a href={`/api/product/backlog/${current}/export.csv`} className={cn(buttonVariants({ variant: 'secondary' }), 'text-[13px] text-foreground no-underline hover:text-foreground')}>
            Exporter
          </a>
        )}
        <Button type="button" className="text-[13px]" disabled={!current || publish.isPending || (data?.unpublishedChanges ?? 0) === 0} onClick={() => publish.mutate()}>
          Publier le backlog
        </Button>
      </PageTitle>

      <div role="tablist" aria-label="Produits" className="flex flex-wrap gap-1 border-b border-border">
        {(data?.products ?? []).map((p) => (
          <button
            key={p.id}
            type="button"
            role="tab"
            aria-selected={p.id === current}
            onClick={() => {
              setProductId(p.id);
              setTeamId('');
            }}
            className={cn(
              '-mb-px cursor-pointer rounded-t-control border border-b-0 px-4 py-2.5 font-[inherit] text-sm',
              p.id === current ? 'border-border bg-card font-bold text-foreground' : 'border-transparent bg-transparent text-foreground-secondary',
            )}
          >
            {p.name}&nbsp;({p.count + p.helpCount})
          </button>
        ))}
      </div>

      {selected && (
        <div role="tablist" aria-label="Vue du produit" className="flex flex-wrap gap-2">
          {(
            [
              ['backlog', 'Ordre de priorité', selected.count],
              ['help', 'Aide & accès', selected.helpCount],
              ['all', 'Voir tout', null],
            ] as const
          ).map(([key, label, n]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={view === key}
              onClick={() => setView(key)}
              className={cn(
                // Same chips as the filters of screen 06.
                'min-h-8 cursor-pointer rounded-control border px-3 font-[inherit] text-[13px]',
                view === key ? 'border-primary bg-accent font-bold text-accent-strong' : 'border-border bg-card text-foreground-secondary',
              )}
            >
              {label}
              {n !== null && <>&nbsp;({n})</>}
            </button>
          ))}
        </div>
      )}

      {view === 'help' && data?.product && <HelpRequestsSection productId={data.product.id} productName={data.product.name} />}
      {view === 'all' && data?.product && <AllRequestsSection key={data.product.id} productId={data.product.id} productName={data.product.name} />}

      {view === 'backlog' && (
        <>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <VersionCard label="Version en cours" version={versions?.current ?? null} hint={`${versions?.current?.requestCount ?? 0} demandes embarquées`} tone="accent" />
          <VersionCard label="Version suivante" version={versions?.next ?? null} hint={`${versions?.next?.requestCount ?? 0} demandes planifiées`} />
          <div className="flex flex-col gap-1 rounded-card border border-border bg-card px-5 py-4">
            <span className="text-[13px] text-muted-foreground">Non planifiées</span>
            <span className="text-[17px] font-bold">{versions?.unplannedCount ?? 0} demandes</span>
            <span className="text-xs text-muted-foreground">À arbitrer</span>
          </div>
        </div>

        <SectionCard
          title={`Ordre de priorité · ${data?.product?.name ?? ''}`}
          toolbar={
            <>
              <span className="text-[13px] text-muted-foreground">{canReorder ? 'Glissez une ligne pour la déplacer' : 'Retirez le filtre d’équipe pour réordonner'}</span>
              {(data?.unpublishedChanges ?? 0) > 0 && (
                <span className="rounded-full bg-warning px-2.5 py-[3px] text-xs font-bold text-warning-foreground">
                  Brouillon · {data!.unpublishedChanges} changement{data!.unpublishedChanges > 1 ? 's' : ''} non publié{data!.unpublishedChanges > 1 ? 's' : ''}
                </span>
              )}
              <div className="ml-auto">
                <SelectField label="Équipe" size="sm" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
                  <option value="">Toutes les équipes</option>
                  {(data?.teams ?? []).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </SelectField>
              </div>
            </>
          }
        >
          <Table minWidth={1100}>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Ordre</TableHead>
                <TableHead>Demande</TableHead>
                <TableHead>Demandeur</TableHead>
                <TableHead>Soumise le</TableHead>
                <TableHead>Impact métier</TableHead>
                <TableHead>Priorité</TableHead>
                <TableHead>Estim.</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((item, index) => (
                <Fragment key={item.id}>
                  {index === firstUnplanned && index > 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="bg-surface-subtle py-2 text-xs font-bold uppercase tracking-[0.04em] text-muted-foreground">
                        Non planifiées · à planifier
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow
                    {...(canReorder ? drag.rowProps(item.id) : {})}
                    className={cn(canReorder && 'cursor-grab', drag.over === item.id && drag.dragged !== item.id && 'bg-accent', drag.dragged === item.id && 'opacity-50')}
                  >
                    <TableCell>
                      <span className="flex items-center gap-1">
                        {canReorder && <GripVertical aria-hidden="true" className="size-4 text-muted-foreground" />}
                        <OrderBadge order={canReorder ? index + 1 : item.draftOrder} />
                        {canReorder && (
                          <span className="flex flex-col">
                            <button type="button" aria-label={`Monter ${item.reference}`} disabled={index === 0} onClick={() => drag.step(item.id, -1)} className="cursor-pointer border-0 bg-transparent p-0 text-muted-foreground disabled:opacity-30">
                              <ArrowUp className="size-3.5" />
                            </button>
                            <button type="button" aria-label={`Descendre ${item.reference}`} disabled={index === filtered.length - 1} onClick={() => drag.step(item.id, 1)} className="cursor-pointer border-0 bg-transparent p-0 text-muted-foreground disabled:opacity-30">
                              <ArrowDown className="size-3.5" />
                            </button>
                          </span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Link to={`/produit/demandes/${item.id}`} className="font-bold text-foreground no-underline hover:text-foreground">
                        {item.title}
                      </Link>
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        {item.reference} · {TYPE_LABELS[item.type]}
                        {item.jiraKey && ` · ${item.jiraKey}`}
                      </span>
                      <TeamsConcernedBadge count={item.teamsConcerned} className="mt-1.5" />
                    </TableCell>
                    <TableCell className="text-foreground-secondary">
                      {shortName(item.requester)}
                      <span className="block text-xs text-muted-foreground">{item.team.name}</span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-foreground-secondary">{formatDate(item.submittedAt)}</TableCell>
                    <TableCell>
                      <ImpactTag impact={item.impact} />
                    </TableCell>
                    <TableCell className="text-foreground-secondary">{item.priority ? PRIORITY_LABELS[item.priority] : '—'}</TableCell>
                    <TableCell className="text-foreground-secondary">{item.estimate ?? '—'}</TableCell>
                    <TableCell className="whitespace-nowrap text-foreground-secondary">{item.version ? `${data?.product?.name ?? ''} ${item.version.number}` : '—'}</TableCell>
                    <TableCell>
                      <StatusPill status={item.status} />
                    </TableCell>
                  </TableRow>
                </Fragment>
              ))}
              {data && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground">
                    Aucune demande qualifiée dans ce backlog.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </SectionCard>
        </>
      )}
      {(saveOrder.isError || publish.isError) && (
        <p role="alert" className="m-0 text-sm font-bold text-destructive">
          {(saveOrder.error ?? publish.error)?.message}
        </p>
      )}
    </>
  );
}
